import { useEffect, useRef, useState } from 'react';
import { RgbaStringColorPicker } from 'react-colorful';
import { cssColorToHex, isTransparent, normalizeColorInput, toRgbaString } from '~/lib/element-selector/color-utils';

interface Props {
  value: string;
  onChange: (css: string) => void;
}

/**
 * Swatch + hex readout; clicking opens a popover with a saturation/hue/alpha
 * picker and an editable hex field. Everything flows through as CSS color
 * strings, so the panel's delta map and the style-edit prompt need no changes.
 * Unparseable computed values (e.g. gradients) fall back to the raw string in
 * the readout and a neutral picker start value.
 */
export function ColorField({ value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [hexDraft, setHexDraft] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const close = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', close);

    // consistent-return flags the early `return;` (open=false, no-op) against
    // the later cleanup return — both are valid useEffect body shapes.
    // eslint-disable-next-line consistent-return
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const transparent = isTransparent(value);
  const hex = cssColorToHex(value);
  const display = transparent ? 'transparent' : (hex ?? value);
  const pickerValue = toRgbaString(value) ?? 'rgba(128, 128, 128, 1)';

  const commitHex = (raw: string) => {
    // Bare hex → prefixed; keywords / rgb() / hsl() pass through so they still
    // apply (the field replaced a raw text input that accepted any CSS color).
    const next = normalizeColorInput(raw);

    if (next) {
      onChange(next);
    }

    setHexDraft(null);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-24 items-center gap-1.5 rounded-md bg-raised border border-hairline px-1.5 py-1 text-left"
        aria-label="Edit color"
      >
        <span
          className="h-4 w-4 shrink-0 rounded-sm border border-hairline"
          style={{
            background: transparent ? 'repeating-conic-gradient(#5a5a64 0% 25%, #2c2c33 0% 50%) 0 0 / 8px 8px' : value,
          }}
        />
        <span className="truncate font-geist-mono text-primary">{display}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-lg border border-hairline bg-surface p-2 shadow-xl">
          <RgbaStringColorPicker color={pickerValue} onChange={onChange} style={{ width: '100%', height: 140 }} />
          <input
            type="text"
            value={hexDraft ?? display}
            onChange={(event) => setHexDraft(event.target.value)}
            onBlur={() => hexDraft !== null && commitHex(hexDraft)}
            onKeyDown={(event) => event.key === 'Enter' && hexDraft !== null && commitHex(hexDraft)}
            className="mt-2 w-full rounded-md bg-raised border border-hairline px-1.5 py-1 font-geist-mono text-primary"
            aria-label="Hex color"
          />
          <button
            type="button"
            onClick={() => onChange('transparent')}
            className="mt-1.5 w-full rounded-md border border-hairline px-1.5 py-1 text-secondary hover:text-primary"
          >
            Transparent
          </button>
        </div>
      )}
    </div>
  );
}
